/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Gauge, RefreshCw, Plus, Pencil, ListTree, Ban, Cpu, Building2, ShieldCheck, Download, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { cn } from '@/lib/utils';
import { useAuth } from '@/lib/auth';
import { ErrorState } from '@/components/UIExtras';
import { getAiUsageConfig, listAiUsageTenants, formatCompact } from '@/lib/aiUsage';
import { QuotaBar, KpiTile, PlanDialog, EventsDialog } from '@/components/admin/aiusage/AiUsageParts';
import { AiPlanUpgradeCard } from '@/components/admin/aiusage/AiPlanUpgradeCard';
import { downloadAiUsageCsv } from '@/lib/aiBilling';

/**
 * AiUsagePage — /admin/ai-usage
 * Contatore AI multi-tenant: per ogni clinica clonata (header x-clinic-id) mostra piano, consumo
 * del ciclo corrente, richieste bloccate e token; permette di cambiare tier/limite senza redeploy.
 */
const currentMonth = () => new Date().toISOString().slice(0, 7);
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString('it-IT', { dateStyle: 'medium' }) : '—');

export default function AiUsagePage() {
  const { user } = useAuth() || {};
  const [month, setMonth] = useState(currentMonth());
  const [data, setData] = useState(null);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [planFor, setPlanFor] = useState(undefined); // undefined=chiuso, null=nuovo, obj=modifica
  const [eventsFor, setEventsFor] = useState(null);
  const [exporting, setExporting] = useState(null); // 'summary' | clinic_id

  const exportCsv = async (clinicId) => {
    setExporting(clinicId || 'summary');
    try {
      const name = await downloadAiUsageCsv(month, clinicId);
      toast.success(`CSV scaricato: ${name}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Export non riuscito');
    } finally { setExporting(null); }
  };

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const [t, c] = await Promise.all([listAiUsageTenants(month), config ? Promise.resolve(config) : getAiUsageConfig()]);
      setData(t); setConfig(c);
    } catch (e) {
      setError(e?.response?.data?.detail || (e?.code === 'ERR_NETWORK' ? 'Rete non disponibile' : 'Impossibile caricare il consumo AI'));
    } finally { setLoading(false); }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- config è una cache: non deve ritriggerare il load
  }, [month]);
  useEffect(() => { load(); }, [load]);

  const items = useMemo(() => data?.items || [], [data]);
  const totals = data?.totals || {};
  const atRisk = items.filter((i) => i.usage_pct >= 80).length;

  if (user && !user.is_admin) {
    return <Card className="rounded-xl"><CardContent className="py-10 text-center text-sm text-muted-foreground" data-testid="ai-usage-not-admin">Accesso riservato agli amministratori.</CardContent></Card>;
  }

  return (
    <div className="space-y-5 max-w-6xl" data-testid="ai-usage-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="h-11 w-11 rounded-xl bg-primary text-primary-foreground flex items-center justify-center"><Gauge className="h-5 w-5" /></div>
          <div>
            <h1 className="font-display font-bold text-xl">Consumo AI per clinica</h1>
            <p className="text-xs text-muted-foreground">Tutte le cliniche white-label passano dalla tua chiave Emergent centrale. Qui vedi chi consuma cosa e imposti i limiti mensili: oltre soglia il backend risponde 402 <b>prima</b> di chiamare l'AI.</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Input type="month" value={month} onChange={(e) => setMonth(e.target.value || currentMonth())} className="h-9 rounded-xl w-[150px] text-xs" aria-label="Mese" data-testid="ai-usage-month" />
          <Button variant="ghost" size="sm" onClick={load} disabled={loading} className="rounded-xl h-9" aria-label="Ricarica" data-testid="ai-usage-reload"><RefreshCw className={cn('h-4 w-4', loading && 'animate-spin')} /></Button>
          <Button variant="outline" size="sm" onClick={() => exportCsv()} disabled={!!exporting || loading} className="rounded-xl h-9" data-testid="ai-usage-export-csv">
            {exporting === 'summary' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />} Esporta CSV
          </Button>
          <Button size="sm" onClick={() => setPlanFor(null)} className="rounded-xl h-9" data-testid="ai-usage-new-tenant"><Plus className="h-4 w-4" /> Nuova clinica</Button>
        </div>
      </div>

      {loading && !data ? (
        <div className="space-y-4"><div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">{[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-20 rounded-xl" />)}</div><Skeleton className="h-72 rounded-xl" /></div>
      ) : error ? (
        <ErrorState message={error} onRetry={load} testId="ai-usage-error" />
      ) : (
        <>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <KpiTile label="Cliniche attive" value={totals.tenants ?? 0} hint={`${atRisk} oltre l'80% della quota`} Icon={Building2} testId="ai-usage-kpi-tenants" />
            <KpiTile label={`Richieste AI · ${month}`} value={formatCompact(totals.requests)} hint="solo chiamate che hanno consumato token" Icon={Cpu} testId="ai-usage-kpi-requests" />
            <KpiTile label="Bloccate (402)" value={formatCompact(totals.blocked)} hint="credito Emergent risparmiato" Icon={Ban} testId="ai-usage-kpi-blocked" />
            <KpiTile label="Token totali" value={formatCompact(totals.tokens_total)} hint={config?.token_counter === 'ready' ? 'conteggio esatto (tiktoken)' : 'stima caratteri/4'} Icon={ShieldCheck} testId="ai-usage-kpi-tokens" />
          </div>

          <Card className="rounded-xl" data-testid="ai-usage-table-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Piani e consumo del ciclo corrente</CardTitle>
              <CardDescription>Il contatore si azzera automaticamente ogni 30 giorni alla data indicata. Modifica tier o limite: effetto immediato.</CardDescription>
            </CardHeader>
            <CardContent className="p-0">
              {items.length === 0 ? (
                <div className="py-10 text-center text-sm text-muted-foreground" data-testid="ai-usage-empty">
                  Nessuna clinica registrata: il primo tenant viene creato automaticamente alla prima chiamata AI, oppure aggiungilo ora.
                  <div className="mt-3"><Button size="sm" onClick={() => setPlanFor(null)} className="rounded-xl"><Plus className="h-4 w-4" /> Nuova clinica</Button></div>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Clinica</TableHead><TableHead>Piano</TableHead><TableHead>Ciclo corrente</TableHead>
                      <TableHead className="text-right">Mese · req</TableHead><TableHead className="text-right">Bloccate</TableHead><TableHead className="text-right">Token</TableHead>
                      <TableHead>Reset</TableHead><TableHead className="text-right">Azioni</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {items.map((t) => (
                      <TableRow key={t.clinic_id} data-testid={`ai-usage-row-${t.clinic_id}`}>
                        <TableCell><div className="font-mono text-xs">{t.clinic_id}</div>{t.notes && <div className="text-[10px] text-muted-foreground truncate max-w-[180px]">{t.notes}</div>}</TableCell>
                        <TableCell><Badge variant="outline" className="text-[10px]">{t.plan_tier}</Badge></TableCell>
                        <TableCell><QuotaBar used={t.current_month_requests_count} limit={t.max_monthly_requests} pct={t.usage_pct} testId={`ai-usage-quota-${t.clinic_id}`} /></TableCell>
                        <TableCell className="text-right text-xs tabular-nums">{formatCompact(t.month_stats.requests)}<div className="text-[10px] text-muted-foreground">T {t.month_stats.by_family?.triage || 0} · V {t.month_stats.by_family?.voice || 0} · E {t.month_stats.by_family?.records || 0}</div></TableCell>
                        <TableCell className={cn('text-right text-xs tabular-nums', t.month_stats.blocked > 0 && 'text-destructive font-semibold')}>{t.month_stats.blocked}</TableCell>
                        <TableCell className="text-right text-xs tabular-nums">{formatCompact(t.month_stats.tokens_total)}<div className="text-[10px] text-muted-foreground">{formatCompact(t.month_stats.tokens_in)} ↑ {formatCompact(t.month_stats.tokens_out)} ↓</div></TableCell>
                        <TableCell className="text-xs whitespace-nowrap">{fmtDate(t.billing_cycle_reset_date)}</TableCell>
                        <TableCell className="text-right whitespace-nowrap">
                          <Button variant="ghost" size="sm" className="h-8 rounded-lg" onClick={() => setEventsFor(t)} aria-label="Telemetria" data-testid={`ai-usage-events-${t.clinic_id}`}><ListTree className="h-4 w-4" /></Button>
                          <Button variant="ghost" size="sm" className="h-8 rounded-lg" onClick={() => exportCsv(t.clinic_id)} disabled={!!exporting} aria-label="Esporta dettaglio CSV" data-testid={`ai-usage-export-${t.clinic_id}`}>{exporting === t.clinic_id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}</Button>
                          <Button variant="ghost" size="sm" className="h-8 rounded-lg" onClick={() => setPlanFor(t)} aria-label="Modifica piano" data-testid={`ai-usage-edit-${t.clinic_id}`}><Pencil className="h-4 w-4" /></Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>

          <AiPlanUpgradeCard onChanged={load} />

          {config && (
            <Card className="rounded-xl" data-testid="ai-usage-config-card">
              <CardHeader className="pb-2"><CardTitle className="text-sm">Route misurate</CardTitle><CardDescription>Solo questi endpoint consumano quota. Auth, appuntamenti, profili e handshake video non vengono conteggiati. Header tenant: <code className="font-mono">{config.header}</code>.</CardDescription></CardHeader>
              <CardContent className="flex flex-wrap gap-1.5">
                {config.metered_routes.map((r, i) => <Badge key={i} variant="secondary" className="font-mono text-[10px] font-normal" title={r.family}>{r.method} {r.pattern.replace(/^\^|\$$/g, '').replace(/\\/g, '')}</Badge>)}
              </CardContent>
            </Card>
          )}
        </>
      )}

      <PlanDialog open={planFor !== undefined} onOpenChange={(o) => !o && setPlanFor(undefined)} tenant={planFor || null} tiers={config?.plan_tiers} onSaved={load} />
      <EventsDialog open={!!eventsFor} onOpenChange={(o) => !o && setEventsFor(null)} tenant={eventsFor} month={month} />
    </div>
  );
}
